import FilterInputWithSuggestions from "@/components/FilterInputWithSuggestions";
import { cn, verifyEmail } from "@/lib/utils";
import { Plus, Users, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { eventFormTone, type EventFormTone } from "./eventFormTone";

type EventAttendeesInputProps = {
    attendees: string[];
    setAttendees: (attendees: string[]) => void;
    tone?: EventFormTone;
};

const EventAttendeesInput = ({
    attendees,
    setAttendees,
    tone = "dark",
}: EventAttendeesInputProps) => {
    const { t } = useTranslation();
    const light = tone === "light";
    const input = (
        <FilterInputWithSuggestions
            className={cn(
                "placeholder:text-sm",
                eventFormTone[tone].placeholder,
                light && "text-base placeholder:text-base",
            )}
            {...(light && {
                placeholder: t(
                    "dashboard.widgets.calendar.event.form.addAttendee",
                ),
            })}
            setValues={setAttendees}
            suggestionListClassName="bg-white text-foreground"
            validator={verifyEmail}
            values={attendees}
        />
    );

    return (
        <div className="flex items-start gap-2">
            {!light && (
                <Users
                    className={cn(
                        "mt-2.5 h-4 w-4 flex-shrink-0",
                        eventFormTone[tone].icon,
                    )}
                />
            )}
            <div
                className={cn(
                    "flex w-full flex-wrap gap-2 border",
                    light ? "rounded-2xl px-3 py-3" : "rounded-md p-3",
                    eventFormTone[tone].border,
                )}
            >
                {attendees?.map((value) => (
                    <p
                        key={value}
                        className={cn(
                            "mb-1 flex w-fit items-center gap-1 rounded-md px-2 py-1 text-xs",
                            eventFormTone[tone].chip,
                            light && "mb-0 gap-2",
                        )}
                    >
                        {value}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setAttendees?.(
                                    attendees?.filter((v) => v !== value),
                                );
                            }}
                            type="button"
                        >
                            <X
                                className={cn(
                                    "cursor-pointer",
                                    eventFormTone[tone].chipRemove,
                                )}
                                size={light ? 16 : 14}
                            />
                        </button>
                    </p>
                ))}
                {light ? (
                    <div className="flex w-full items-center gap-2 px-1 text-assistant-muted">
                        <Plus
                            aria-hidden
                            className="size-5 flex-none"
                            strokeWidth={1.5}
                        />
                        <div className="min-w-0 flex-1">{input}</div>
                    </div>
                ) : (
                    input
                )}
            </div>
        </div>
    );
};

export default EventAttendeesInput;
